import * as React from 'react';
import { styled, alpha } from '@mui/material/styles';
import {
  InputBase,
  Autocomplete,
  InputBaseProps,
  ListItem,
  Divider,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Box
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import debounce from 'lodash/debounce';
import { SearchResult } from '../../model/common-types';
import { useEffect, useMemo } from 'react';
import { HeaderSearchProps } from './header-search';
import { useQuery } from 'react-query';

const Search = styled('div')(({ theme }) => ({
  position: 'relative',
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.grey[400], 0.15),
  '&:hover': {
    backgroundColor: alpha(theme.palette.grey[400], 0.25)
  },
  paddingRight: 20,
  marginLeft: 20,
  width: '100%',
  [theme.breakpoints.up('xs')]: {
    marginLeft: theme.spacing(1),
    width: 'auto'
  }
}));

const SearchIconWrapper = styled('div')(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: '100%',
  position: 'absolute',
  pointerEvents: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
}));

export const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: 'inherit',
  '& .MuiInputBase-input': {
    padding: theme.spacing(1, 1, 1, 0),
    // vertical padding + font size from searchIcon
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      transition: theme.transitions.create('width'),
      width: '20ch',
      '&:focus': {
        width: '25ch'
      }
    },
    [theme.breakpoints.up('md')]: {
      transition: theme.transitions.create('width'),
      width: '20ch',
      '&:focus': {
        width: '45ch'
      }
    },
    [theme.breakpoints.up('lg')]: {
      transition: theme.transitions.create('width'),
      width: '30ch',
      '&:focus': {
        width: '60ch'
      }
    }
  }
}));

export function SearchInput(props: InputBaseProps) {
  return <StyledInputBase {...props} />;
}

interface SearchAutocompleteProps extends HeaderSearchProps {
  focused?: boolean;
  onClose?: () => void;
}

export function SearchAutocomplete(props: SearchAutocompleteProps) {
  const [open, setOpen] = React.useState(false);
  //react-query will cache values so isFetching only fires after stale time has elapsed, so we have this separate boolean to indicate loading on screen
  const [loading, setLoading] = React.useState(false);
  const [debouncedInput, setDebouncedInput] = React.useState<string>('');
  const [value, setValue] = React.useState<SearchResult | null>(null);
  const [inputValue, setInputValue] = React.useState('');

  //react-query will cache values so isFetching only fires after stale time has elapsed
  const { data, isFetching, isError } = useQuery(
    ['site_search', debouncedInput],
    () => props.handleSearch(debouncedInput),
    {
      placeholderData: [
        {
          link: '',
          text: 'Searching...',
          title: ''
        }
      ],
      retry: 1,
      enabled: debouncedInput != '',
      staleTime: 30000,
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false
    }
  );

  const debounceText = useMemo(
    () =>
      debounce(async (value: string) => {
        setDebouncedInput(value);
        setLoading(false);
      }, 500),
    []
  );

  //cancel debounced on unmount
  useEffect(() => {
    return () => {
      debounceText.cancel();
    };
  }, []);

  useEffect(() => {
    if (value) {
      return undefined;
    }

    if (inputValue && inputValue.length > 2) {
      debounceText(inputValue);
      setLoading(true);
      setOpen(true);
    } else {
      setOpen(false);
      setDebouncedInput('');
      setLoading(false);
    }
  }, [inputValue]);

  const onValueChange = (_event, searchValue: SearchResult) => {
    setOpen(false);
    setLoading(false);
    setValue(searchValue);

    if (props.onLoading) {
      props.onLoading(true);
    }

    window.location.href = searchValue.link;
  };

  const getNoResultsItem = () => {
    if (isError) {
      return {
        link: '',
        text: 'There was a problem finding results...',
        title: ''
      };
    }
    return {
      link: '',
      text: loading ? 'Searching...' : 'No results found.',
      title: ''
    };
  };

  return (
    <Autocomplete
      id="header-search"
      fullWidth
      open={open}
      groupBy={(option) => option.title}
      value={value}
      onInputChange={(_event, newInputValue) => {
        setInputValue(newInputValue);
      }}
      onChange={onValueChange}
      getOptionDisabled={(option) => !option.link}
      onClose={() => {
        setOpen(false);
        if (props.onClose) {
          props.onClose();
        }
      }}
      renderOption={(props, option) => (
        <React.Fragment key={option.text}>
          {option.forceSearch ? (
            <>
              <Divider sx={{ my: 1 }} />
              <ListItem {...props}>
                <ListItemIcon>
                  <SearchIcon />
                </ListItemIcon>
                <ListItemText primary={`Find more results for "${option.text}"`} />
              </ListItem>
            </>
          ) : (
            <ListItem {...props}>
              <ListItemText primary={option.text} secondary={option.secondary} />
            </ListItem>
          )}
        </React.Fragment>
      )}
      filterOptions={(options, params) => {
        const { inputValue } = params;
        if (options.length == 0) {
          options.push(getNoResultsItem());
        }
        // Add "search" action
        if (props.showSearchMore && inputValue !== '') {
          options.push({
            link: `${props.searchUrl ?? '/search/site'}/${inputValue}`,
            text: inputValue,
            forceSearch: true,
            title: ''
          });
        }

        return options;
      }}
      isOptionEqualToValue={(option, value) => option.text === value.text}
      getOptionLabel={(option) => option.text}
      options={data || []}
      loading={loading}
      renderInput={(params) => (
        <Search ref={params.InputProps.ref}>
          <SearchIconWrapper>
            <SearchIcon />
          </SearchIconWrapper>
          <SearchInput
            inputRef={props.focused ? (input) => input && input.focus() : undefined}
            tabIndex={0}
            fullWidth
            placeholder={props.placeholder ?? 'Search hotels, tours, and destinations'}
            inputProps={params.inputProps}
            endAdornment={
              <React.Fragment>
                {(loading || isFetching) && inputValue && inputValue.length > 2 ? (
                  <CircularProgress color="inherit" size={20} />
                ) : (
                  <Box sx={{ width: '20px' }} />
                )}
              </React.Fragment>
            }
          />
        </Search>
      )}
    />
  );
}
